// API DTOs — source of truth for request/response shapes. The Hono handlers
// (apps/worker/src/api) and the React hooks (apps/web/src/api/hooks.ts) both
// import from here. Change types here first, then fix both sides.

import { z } from "zod";

export interface HealthResponse {
  ok: true;
  service: "etus-mindmap";
  time: string;
}

export interface MeResponse {
  email: string;
  role: UserRole;
}

export type UserRole = "member" | "admin";

// ---- Mind maps ----------------------------------------------------------

// A map's content is an opaque JSON document as far as the API is concerned
// (nodes/edges live client-side); the Worker validates only the envelope.
export const mindmapDocSchema = z.object({
  nodes: z.array(z.record(z.unknown())),
  edges: z.array(z.record(z.unknown())),
});
export type MindmapDoc = z.infer<typeof mindmapDocSchema>;

// Client-side shape of a node inside MindmapDoc.nodes (Fase 1: árvore
// hierárquica — edges são derivadas de parentId no cliente). The index
// signature keeps it assignable to the loose envelope above.
export interface MindNode {
  id: string;
  label: string;
  parentId: string | null;
  // Ramo recolhido no editor (persiste no doc, como no XMind).
  collapsed?: boolean;
  // ---- Fase 4: modo grafo livre (concept map / fluxograma) ----
  // Posição livre persistida (só usada em tipos de grafo; tipos de árvore
  // ignoram e recalculam o layout). Semeada ao trocar de árvore p/ grafo.
  x?: number;
  y?: number;
  // Forma do nó no fluxograma; ausente = retângulo (processo).
  shape?: NodeShape;
  // ---- Fase 3: conteúdo rico ----
  // Notas em markdown leve (renderizadas como texto pré-formatado por ora).
  notes?: string;
  // Link externo clicável no nó.
  link?: string;
  // Cor do ramo (hex da paleta do editor); descendentes herdam a cor do
  // ancestral mais próximo que tiver uma.
  color?: string;
  status?: NodeStatus;
  comments?: NodeComment[];
  // Metadados dos arquivos no R2 (o binário vive em maps/{mapId}/{id}).
  attachments?: NodeAttachment[];
  // Atalho para outro mapa: uuid do mapa alvo. O nó vira um link de navegação
  // (ícone no nó → abre /maps/:linkedMapUuid). Referência solta: se o alvo for
  // apagado, o atalho fica "quebrado" e o editor mostra isso.
  linkedMapUuid?: string;
  // Tipo semântico do nó no fluxograma (kind === "flow"). Determina forma, cor,
  // topologia de handles e validação. Ausente em mapas de flow antigos → inferido
  // de `shape` via resolveFlowType. Ignorado nos outros tipos de mapa.
  flowType?: FlowType;
  [key: string]: unknown;
}

export type NodeStatus = "priority" | "doing" | "done" | "flag";

// Formas de nó no fluxograma (legado). rect = processo; round = início/fim
// (estádio); diamond = decisão. Substituído por `flowType`; mantido só para
// back-compat (inferência ao carregar mapas de flow antigos).
export type NodeShape = "rect" | "round" | "diamond";

// ---- Fluxograma: tipos de nó (vocabulário ANSI) --------------------------

export type FlowType =
  | "start"
  | "end"
  | "process"
  | "decision"
  | "io"
  | "subprocess"
  | "database"
  | "document"
  | "connector";

export const FLOW_TYPES_ORDER: FlowType[] = [
  "start",
  "end",
  "process",
  "decision",
  "io",
  "subprocess",
  "database",
  "document",
  "connector",
];

// Forma de renderização (o desenho fica no web; aqui é só a tag semântica).
export type FlowShapeKind =
  | "round" // estádio (início/fim)
  | "rect" // processo
  | "diamond" // decisão
  | "parallelogram" // entrada/saída
  | "doubleBar" // subprocesso (retângulo c/ barras duplas)
  | "cylinder" // banco de dados
  | "document" // documento (base ondulada)
  | "circle"; // conector

export type FlowCategory = "terminal" | "process" | "io" | "control";

export interface FlowTypeMeta {
  label: string;
  category: FlowCategory;
  shapeKind: FlowShapeKind;
  // Token de cor tailwind base (ex.: "emerald", "rose"); o web mapeia p/ classes.
  color: string;
  // Restrições de conexão (validadas em isValidConnection no cliente).
  noIncoming?: boolean; // start: não pode ser alvo
  noOutgoing?: boolean; // end: não pode ser origem
  // Ramos rotulados (decision). Reusa handle ids EXISTENTES (sr/sb) — nunca criar
  // ids novos, senão as arestas antigas ficam órfãs.
  branches?: Array<{ handleId: string; label: string }>;
}

// Config puro-dados dos tipos de nó (sem JSX — shared é importado pelo worker).
export const FLOW_TYPES: Record<FlowType, FlowTypeMeta> = {
  start: { label: "Início", category: "terminal", shapeKind: "round", color: "emerald", noIncoming: true },
  end: { label: "Fim", category: "terminal", shapeKind: "round", color: "rose", noOutgoing: true },
  process: { label: "Processo", category: "process", shapeKind: "rect", color: "slate" },
  decision: {
    label: "Decisão",
    category: "control",
    shapeKind: "diamond",
    color: "amber",
    branches: [
      { handleId: "sr", label: "Sim" },
      { handleId: "sb", label: "Não" },
    ],
  },
  io: { label: "Entrada/Saída", category: "io", shapeKind: "parallelogram", color: "sky" },
  subprocess: { label: "Subprocesso", category: "process", shapeKind: "doubleBar", color: "violet" },
  database: { label: "Banco de dados", category: "io", shapeKind: "cylinder", color: "cyan" },
  document: { label: "Documento", category: "io", shapeKind: "document", color: "slate" },
  connector: { label: "Conector", category: "control", shapeKind: "circle", color: "slate" },
};

export function isFlowType(v: unknown): v is FlowType {
  return typeof v === "string" && v in FLOW_TYPES;
}

// Infere o flowType de um nó de flow. Mapas antigos só têm `shape` (rect/round/
// diamond): rect→process, diamond→decision, round→start. round é ambíguo
// (start/end indistinguíveis por shape) — assume start; só afeta flow antigos e o
// tipo agora é fixo após a criação. Resolver LAZY no render, não reescrever o doc.
export function resolveFlowType(node: { flowType?: FlowType; shape?: NodeShape }): FlowType {
  if (node.flowType && isFlowType(node.flowType)) return node.flowType;
  if (node.shape === "diamond") return "decision";
  if (node.shape === "round") return "start";
  return "process";
}

// Aresta manual do modo grafo (concept map / fluxograma). Vive em doc.edges;
// nos tipos de árvore as arestas são derivadas de parentId e este campo é
// ignorado (mas preservado, para a troca de tipo não ser destrutiva).
export interface MindEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
  // Lados de conexão (React Flow handle ids) para renderizar a aresta estável.
  sourceHandle?: string;
  targetHandle?: string;
  // Mantém a aresta atribuível ao envelope solto do doc (edges: Record[]).
  [key: string]: unknown;
}

export interface NodeComment {
  id: string;
  author: string; // e-mail de quem comentou
  text: string;
  createdAt: number; // epoch ms
}

export interface NodeAttachment {
  id: string;
  name: string;
  size: number;
  contentType: string;
}

// Tipo de mapa = layout aplicado sobre o MESMO doc (árvore). O binário do doc
// não muda entre os tipos; só a função de posicionamento no cliente. `edges`
// livres do doc ficam reservadas para tipos de grafo futuros (concept map).
export type MapKind =
  | "mindmap"
  | "mindmap-bi"
  | "org"
  | "timeline"
  | "matrix"
  | "concept"
  | "flow";

export const MAP_KINDS: MapKind[] = [
  "mindmap",
  "mindmap-bi",
  "org",
  "timeline",
  "matrix",
  "concept",
  "flow",
];

export const MAP_KIND_LABELS: Record<MapKind, string> = {
  mindmap: "Mapa mental",
  "mindmap-bi": "Mapa mental duplo",
  org: "Organograma",
  timeline: "Linha do tempo",
  matrix: "Matriz 2×2",
  concept: "Mapa conceitual",
  flow: "Fluxograma",
};

export function isMapKind(v: unknown): v is MapKind {
  return typeof v === "string" && (MAP_KINDS as string[]).includes(v);
}

// Tipos de grafo livre (nós posicionados à mão + arestas manuais). Os demais
// são tipos de árvore (estrutura por parentId + layout automático).
export function isGraphKind(kind: MapKind): boolean {
  return kind === "concept" || kind === "flow";
}

// ---- Colaboração (Fase 5) -----------------------------------------------

export type OrgRole = "owner" | "admin" | "member";

// Onde o mapa vive: personal | org | team. `shared` NÃO é onde o mapa vive —
// é um "espaço" só de leitura no cliente que lista os mapas compartilhados
// comigo (o header X-Space aceita "shared" para essa listagem).
export type SpaceType = "personal" | "org" | "team" | "shared";

// ---- Compartilhamento por usuário (Fase 6) ----
export type ShareRole = "viewer" | "editor";
// Permissão efetiva sobre um mapa. `owner` = pode gerenciar (apagar, compartilhar).
export type MapPermission = "viewer" | "editor" | "owner";

export interface MapShare {
  email: string;
  role: ShareRole;
  createdAt: number;
}
export interface ShareMapRequest {
  email: string;
  role?: ShareRole;
}

// ---- Links públicos anônimos (Fase 7) ----
export interface MapLink {
  id: number;
  token: string;
  role: ShareRole;
  createdAt: number;
}
export interface CreateLinkRequest {
  role?: ShareRole;
}
// Resposta do caminho anônimo GET /api/shared/:token — só o essencial para
// renderizar/editar o mapa; nada de ownerEmail/space/id (evita vazamento).
export interface PublicMapResponse {
  title: string;
  kind: MapKind;
  doc: MindmapDoc;
  permission: MapPermission; // viewer | editor (nunca owner via link)
  // Revisão do doc (trava otimista) — ver MindmapDetail.rev.
  rev: number;
}

export interface SpaceRef {
  type: SpaceType;
  id: number | null; // null quando personal
}

export interface Organization {
  id: number;
  slug: string;
  name: string;
  myRole: OrgRole;
  // Branding (Fase 5.1): logo (buscado via blob autenticado em /api/orgs/:id/logo
  // quando hasLogo) e cor primária (hex, aplicada ao vivo no espaço da org).
  hasLogo: boolean;
  primaryColor: string | null;
  createdAt: number;
}

export interface UpdateOrgRequest {
  name?: string;
  primaryColor?: string | null; // hex "#rrggbb" ou null para limpar
}

export interface Team {
  id: number;
  name: string;
  orgId: number | null; // null = time sem organização (cross-org)
  myRole: OrgRole;
  createdAt: number;
}

// Espaços a que o usuário pertence (alimenta o switcher da sidebar).
export interface SpacesResponse {
  orgs: Organization[];
  teams: Team[];
}

export interface Member {
  email: string;
  role: OrgRole;
  addedAt: number;
}

export interface CreateOrgRequest {
  name: string;
  slug?: string;
}

export interface CreateTeamRequest {
  name: string;
  orgId?: number | null;
}

export interface InviteRequest {
  email: string;
  role?: OrgRole;
}

export interface InviteResponse {
  token: string;
  expiresAt: number;
}

// Preview de um convite (rota pública por token), para a tela de aceite.
export interface InvitePreview {
  scope: "org" | "team";
  scopeName: string;
  email: string;
  role: OrgRole;
  valid: boolean; // false se expirado/aceito/inexistente
}

export const ORG_ROLES: OrgRole[] = ["owner", "admin", "member"];
export function isOrgRole(v: unknown): v is OrgRole {
  return typeof v === "string" && (ORG_ROLES as string[]).includes(v);
}

export interface MindmapSummary {
  id: number;
  // UUID público usado na navegação/URLs (o id sequencial fica interno).
  uuid: string;
  title: string;
  ownerEmail: string;
  kind: MapKind;
  spaceType: SpaceType;
  spaceId: number | null;
  createdAt: number; // unix seconds
  updatedAt: number;
}

// Título padrão de um mapa novo (editável inline no editor).
export const DEFAULT_MAP_TITLE = "new document";

export interface MindmapDetail extends MindmapSummary {
  doc: MindmapDoc;
  // Permissão do usuário atual sobre este mapa (o editor usa para read-only).
  permission: MapPermission;
  // Revisão do doc (trava otimista): incrementa a cada PUT que altera `doc`.
  // O editor guarda e reenvia como baseRev; mismatch = 409 conflict.
  rev: number;
}

export interface CreateMindmapRequest {
  title?: string; // opcional: default DEFAULT_MAP_TITLE
  kind?: MapKind;
  doc?: MindmapDoc; // opcional: import cria já com conteúdo (validado no worker)
}

// ---- Export / import (round-trip lossless) ---------------------------------
// Formato próprio do app: o único que preserva TODOS os tipos (árvore e grafo),
// posições x/y e conteúdo rico (notas/link/cores/status/comentários/anexos).
// PNG/PDF são só saída visual, não round-trip.
export const MAP_EXPORT_FORMAT = "etus-mindmap";
export const MAP_EXPORT_VERSION = 1;

export interface MapExportFile {
  format: typeof MAP_EXPORT_FORMAT; // "etus-mindmap" — assinatura do arquivo
  version: number; // MAP_EXPORT_VERSION
  kind: MapKind;
  title: string;
  doc: MindmapDoc;
}

export interface UpdateMindmapRequest {
  title?: string;
  doc?: MindmapDoc;
  kind?: MapKind;
  // Trava otimista ao enviar `doc`: rev que o cliente leu por último. O Worker
  // só aplica se ainda for o rev atual (senão 409 conflict). Ausente = sem
  // trava (back-compat; título/kind nunca exigem).
  baseRev?: number;
}
